import { useState } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money } from '../components/SharedComponents'

/* ============ CoCafe Host — Workspace (Listing) ============ */
function PhotoTile({ p, onCover, onRemove }) {
  const [editing, setEditing] = useState(false);
  return (
    <div style={{ position: "relative", borderRadius: "var(--r)", overflow: "hidden", aspectRatio: "4/3", border: "1px solid var(--border)", cursor: "pointer", group: "photo" }}>
      <img src={p.src} alt={p.label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(transparent 55%, rgba(20,16,10,.6))" }} />
      {p.cover && <span className="badge" style={{ position: "absolute", top: 10, left: 10, background: "#fff", color: "var(--ink)" }}><HI.star s={11} style={{ color: "var(--amber)" }} />Cover</span>}
      <span style={{ position: "absolute", bottom: 10, left: 10, color: "#fff", fontSize: 12, fontWeight: 600 }}>{p.label}</span>
      {!editing && (
        <button className="tb-icon" style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }} title="Edit" onClick={() => setEditing(true)}><HI.edit s={16} /></button>
      )}
      {editing && (
        <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {!p.cover && <button className="tb-icon" style={{ width: 32, height: 32, background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }} title="Make cover" onClick={() => { onCover(); setEditing(false); }}><HI.star s={16} /></button>}
          <button className="tb-icon" style={{ width: 32, height: 32, background: "rgba(255,255,255,.9)", color: "var(--red)", display: "flex", alignItems: "center", justifyContent: "center" }} title="Remove" onClick={() => { onRemove(); setEditing(false); }}><HI.trash s={16} /></button>
        </div>
      )}
    </div>
  );
}

function Workspace({ toast }) {
  const loc = H.locations[0];
  const [visible, setVisible] = useState(true);
  const [photos, setPhotos] = useState(H.gallery);
  const [amenities, setAmenities] = useState(H.amenityGroups);
  const [wifi, setWifi] = useState({ down: 240, up: 90, tested: "41 days ago" });
  const [seats, setSeats] = useState(loc.seats);
  const [dirty, setDirty] = useState(false);

  const setCover = (id) => { setPhotos((ps) => ps.map((p) => ({ ...p, cover: p.id === id }))); setDirty(true); toast("Cover photo updated"); };
  const remove = (id) => { setPhotos((ps) => ps.filter((p) => p.id !== id)); setDirty(true); };
  const toggleAm = (gi, k) => { setAmenities((g) => g.map((grp, i) => i === gi ? { ...grp, items: grp.items.map((it) => it.k === k ? { ...it, on: !it.on } : it) } : grp)); setDirty(true); };

  const amOnCount = amenities.reduce((s, g) => s + g.items.filter((i) => i.on).length, 0);

  return (
    <div className="page fade-in">

      {/* wifi re-test reminder */}
      <div style={{ marginBottom: 20 }}>
        <Banner tone="amber" icon={<HI.wifi s={18} />} title="Time to re-test your WiFi"
          action={<button className="btn btn-ghost btn-sm" onClick={() => { setWifi({ ...wifi, tested: "just now" }); toast("Speed test started", "We'll update your badge in a minute"); }}>Run speed test</button>}>
          Your verified speed is {wifi.tested}. Guests trust fresh numbers — re-test to keep your “Verified WiFi” badge.
        </Banner>
      </div>

      <div className="row g-4">
        {/* LEFT */}
        <div className="col-12 col-lg-8">
          {/* details */}
          <div className="card" style={{ marginBottom: 20 }}>
            <CardHead title="Listing details" />
            <div className="card-pad col g14">
              <div className="field"><span className="lbl">Workspace name</span><input className="inp" defaultValue={loc.name} onChange={() => setDirty(true)} /></div>
              <div className="field"><span className="lbl">Tagline</span><input className="inp" defaultValue="A skylit loft built for deep-focus mornings" onChange={() => setDirty(true)} />
                <span className="hint">Shown under your name in search. Keep it under 60 characters.</span></div>
              <div className="field"><span className="lbl">About this space</span>
                <textarea className="ta" onChange={() => setDirty(true)} defaultValue="A skylit loft of warm oak desks and slow filter coffee. Built for deep-focus mornings — the quietest room in Nungambakkam. Power at every seat, a phone booth for calls, and bottomless filter coffee while you work." /></div>
              <div className="field"><span className="lbl">Address</span><input className="inp" defaultValue={loc.address} onChange={() => setDirty(true)} /></div>
            </div>
          </div>

          {/* photos */}
          <div className="card" style={{ marginBottom: 20 }}>
            <CardHead title="Photos" sub={photos.length + " photos · drag to reorder"}
              right={<button className="btn btn-soft btn-sm" onClick={() => toast("Upload photos", "Drag images here or browse")} aria-label="Add photos"><HI.upload s={14} /> Add</button>} />
            <div className="card-pad">
              {photos.length === 0 ? (
                <Empty icon={<HI.image s={22} />} title="No photos yet" desc="Listings with 5+ photos get up to 2× more bookings. Add your best room shots." action={<button className="btn btn-pri btn-sm"><HI.upload s={14} /> Upload photos</button>} />
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                  {photos.map((p) => <PhotoTile key={p.id} p={p} onCover={() => setCover(p.id)} onRemove={() => remove(p.id)} />)}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* AMENITIES + VISIBILITY/SEATING - 2 COLUMN */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px', marginTop: '0' }}>
          {/* LEFT: Amenities - CENTERED COMPACT */}
          <div className="card">
            <CardHead title="Amenities" sub={amOnCount + " offered"} />
            <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', textAlign: 'center' }}>
              {amenities.map((g, gi) => (
                <div key={g.group} style={{ width: '100%' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, marginBottom: 6, color: 'var(--muted)' }}>{g.group}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                    {g.items.map((it) => (
                      <button key={it.k} onClick={() => toggleAm(gi, it.k)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: "6px 8px", borderRadius: "var(--r)", border: "1px solid " + (it.on ? "var(--green)" : "var(--border-2)"), background: it.on ? "var(--green-weaker)" : "var(--surface)", textAlign: "center", cursor: "pointer", fontSize: '10px' }}>
                        <span style={{ width: 16, height: 16, borderRadius: 4, background: it.on ? "var(--green)" : "var(--surface-3)", color: it.on ? "#fff" : "var(--faint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {it.on ? <HI.check s={10} /> : <HI.plus s={10} />}
                        </span>
                        <span style={{ fontWeight: 600, color: it.on ? "var(--ink)" : "var(--ink-2)" }}>{it.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Visibility + Seating - COMPACT STACKED */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* visibility - CENTERED */}
            <div className="card">
              <CardHead title="Visibility" />
              <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 }}>
                <span style={{ width: 28, height: 28, borderRadius: 6, background: visible ? "var(--green-weak)" : "var(--surface-3)", color: visible ? "var(--green)" : "var(--muted)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  {visible ? <HI.eye s={14} /> : <HI.eyeOff s={14} />}
                </span>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: 2 }}>{visible ? "Listed" : "Hidden"}</div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{visible ? "Guests can book" : "Not visible"}</div>
                </div>
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => { setVisible((v) => !v); toast(visible ? "Listing paused" : "Listing is live", visible ? "You won't get new bookings until you re-list" : "Guests can book you again"); }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--green)' }}
                  aria-label={visible ? "Hide listing from search" : "List and make bookable"}
                />
              </div>
            </div>

            {/* seating */}
            <div className="card">
              <CardHead title="Seating capacity" />
              <div style={{ padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 650, marginBottom: 2 }}>Desks</div>
                    <div style={{ fontSize: '9px', color: 'var(--muted)' }}>Max guests</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-soft btn-icon btn-sm" onClick={() => { setSeats((s) => Math.max(1, s - 1)); setDirty(true); }} style={{ padding: '2px 4px' }} aria-label="Decrease bookable desks"><HI.minus s={12} /></button>
                    <span className="mono" style={{ fontWeight: 700, fontSize: 16, width: 28, textAlign: "center", whiteSpace: "nowrap" }}>{seats}</span>
                    <button className="btn btn-soft btn-icon btn-sm" onClick={() => { setSeats((s) => s + 1); setDirty(true); }} style={{ padding: '2px 4px' }} aria-label="Increase bookable desks"><HI.plus s={12} /></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT - WiFi + Policies */}
        <div className="col-12 col-lg-4">
          {/* wifi + policies - 2 COLUMN SIDE-BY-SIDE */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* wifi - CENTERED COMPACT */}
            <div className="card">
              <CardHead title="WiFi" right={<span className="badge green" style={{ fontSize: '10px', padding: '2px 4px', minWidth: '20px', textAlign: 'center' }}>✓</span>} />
              <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)', marginBottom: 2, fontWeight: 600 }}>Download</div>
                  <div className="mono" style={{ fontWeight: 700, fontSize: '15px' }}>{wifi.down}<span style={{ fontSize: '8px', color: 'var(--muted)', marginLeft: '2px' }}>Mbps</span></div>
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)', marginBottom: 2, fontWeight: 600 }}>Upload</div>
                  <div className="mono" style={{ fontWeight: 700, fontSize: '15px' }}>{wifi.up}<span style={{ fontSize: '8px', color: 'var(--muted)', marginLeft: '2px' }}>Mbps</span></div>
                </div>
                <div style={{ fontSize: '8px', color: 'var(--muted)', paddingTop: 6, borderTop: '1px solid var(--border)', width: '100%' }}>{wifi.tested}</div>
              </div>
            </div>

            {/* policies - COMPACT */}
            <div className="card" style={{ padding: '8px 10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: 6 }}>Policies</div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: '9px', fontWeight: 600, color: 'var(--muted)' }}>Cancellation</div>
                <select className="sel" onChange={() => setDirty(true)} defaultValue="flex" style={{ fontSize: '11px', padding: '5px 8px', width: '100%', maxWidth: '120px' }}>
                  <option value="flex">Flexible</option>
                  <option value="mod">Moderate</option>
                  <option value="strict">Strict</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Workspace;
